import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
    requestJobVerification, 
    verifyAndPostJob,
    clearJobPostingsState,
    clearError,
    clearSuccess,
    selectCreateStatus,
    selectPostStatus,
    selectError,
    selectSuccess,
    selectVerificationData,
    selectCreatedJobs,
    selectIsUserVerified,
    selectVerificationExpiry,
    selectTotalPendingJobs
} from '../_store/jobpostingsSlice';
import Powered_by_logo from "../assets/images/Powered_by_logo.png";
import UAVSphere_logo from "../assets/images/UAVspherelogo.png";
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';

export const JobPostings = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    // Redux state selectors
    const createStatus = useSelector(selectCreateStatus);
    const postStatus = useSelector(selectPostStatus);
    const error = useSelector(selectError);
    const success = useSelector(selectSuccess);
    const verificationData = useSelector(selectVerificationData);
    const createdJobs = useSelector(selectCreatedJobs);
    const isUserVerified = useSelector(selectIsUserVerified);
    const verificationExpiry = useSelector(selectVerificationExpiry);
    const totalPendingJobs = useSelector(selectTotalPendingJobs);
    
    // Local State
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        contactNumber: '',
        location: '',
        description: ''
    });
    
    const [showOtpStep, setShowOtpStep] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [errors, setErrors] = useState({});
    const [countdown, setCountdown] = useState(0);
    const [resendDisabled, setResendDisabled] = useState(false);
    
    // Location Autocomplete State
    const [locationValue, setLocationValue] = useState(null);
    const [selectedLocationDetails, setSelectedLocationDetails] = useState(null);
    
    // Local CAPTCHA State
    const [captchaInput, setCaptchaInput] = useState('');
    const [localCaptchaText, setLocalCaptchaText] = useState('');
    const [localCaptchaImage, setLocalCaptchaImage] = useState('');

    const isSubmitting = createStatus === 'loading' || postStatus === 'loading';

    // Google Places Styles
    const googlePlacesStyles = {
        control: (provided) => ({
            ...provided,
            width: '100%',
            border: '1px solid #ddd',
            borderRadius: '4px',
            minHeight: '38px',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#ddd',
            },
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
        input: (provided) => ({
            ...provided,
            fontSize: '0.9rem',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#f0f0f0' : 'white',
            color: '#333',
            padding: '10px',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#999',
            fontSize: '0.9rem',
        }),
    };

    // Local CAPTCHA Generator (Canvas-based)
    const generateLocalCaptcha = useCallback(() => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; 
        let text = '';
        for (let i = 0; i < 6; i++) {
            text += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 70;
        const ctx = canvas.getContext('2d');

        // Background
        ctx.fillStyle = '#f8f9fa';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Add noise (dots)
        for (let i = 0; i < 50; i++) {
            ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.2})`;
            ctx.beginPath();
            ctx.arc(Math.random() * 200, Math.random() * 70, 1, 0, Math.PI * 2);
            ctx.fill();
        }

        // Draw Text
        ctx.font = 'bold 30px Arial';
        ctx.fillStyle = '#333';
        ctx.textBaseline = 'middle';
        
        for (let i = 0; i < text.length; i++) {
            ctx.save();
            ctx.translate(30 + i * 25, 35);
            ctx.rotate((Math.random() - 0.5) * 0.3);
            ctx.fillText(text[i], 0, 0);
            ctx.restore();
        }

        setLocalCaptchaText(text);
        setLocalCaptchaImage(canvas.toDataURL());
    }, []);

    // Initialize CAPTCHA on mount
    useEffect(() => {
        generateLocalCaptcha();
    }, [generateLocalCaptcha]);
    
    // Countdown timer logic
    useEffect(() => {
        if (verificationExpiry) {
            const expiryTime = new Date(verificationExpiry).getTime();
            const updateCountdown = () => {
                const now = new Date().getTime();
                const diff = Math.max(0, Math.floor((expiryTime - now) / 1000));
                setCountdown(diff);
                if (diff === 0) setResendDisabled(false);
            };
            updateCountdown();
            const interval = setInterval(updateCountdown, 1000);
            return () => clearInterval(interval);
        }
    }, [verificationExpiry]);
    
    // Handle verification request success
    useEffect(() => {
        if (createStatus === 'succeeded') {
            const userVerified = verificationData?.data?.user_verified || false;
            if (!userVerified) {
                setShowOtpStep(true);
                setResendDisabled(true);
            } else {
                setShowSuccessModal(true);
                const timer = setTimeout(() => {
                    setShowSuccessModal(false);
                    dispatch(clearSuccess());
                }, 5000);
                return () => clearTimeout(timer);
            }
        }
    }, [createStatus, verificationData, dispatch]);
    
    // Handle post verification success
    useEffect(() => {
        if (postStatus === 'succeeded' && createdJobs.length > 0) {
            setShowOtpStep(false);
            setShowSuccessModal(true);
            setOtpCode('');
            setCaptchaInput('');
            setLocationValue(null);
            setSelectedLocationDetails(null);
            setFormData({ name: '', email: '', contactNumber: '', location: '', description: '' });
            generateLocalCaptcha();
            const timer = setTimeout(() => {
                setShowSuccessModal(false);
                dispatch(clearSuccess());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [postStatus, createdJobs, dispatch, generateLocalCaptcha]);
    
    useEffect(() => {
        return () => {
            dispatch(clearJobPostingsState());
        };
    }, [dispatch]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Job title is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!emailRegex.test(formData.email)) newErrors.email = 'Valid email required';
        if (!formData.contactNumber.trim()) newErrors.contactNumber = 'Contact number is required';
        
        // Enhanced location validation
        if (!formData.location.trim()) {
            newErrors.location = 'Location is required';
        } else if (!selectedLocationDetails) {
            newErrors.location = 'Please select a valid location from the suggestions';
        }
        
        if (formData.description.trim().length < 20) newErrors.description = 'Description must be at least 20 characters';
        if (!captchaInput.trim()) newErrors.captcha = 'Please enter the CAPTCHA code';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Submit with Location Validation
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        
        // Local Captcha Guard
        if (captchaInput !== localCaptchaText) {
            setErrors(prev => ({ ...prev, captcha: 'Invalid captcha code. Please try again.' }));
            generateLocalCaptcha();
            setCaptchaInput('');
            return;
        }

        dispatch(clearError());
        try {
            // Include location details in the submission
            const jobData = { 
                ...formData, 
                captchaInput,
                locationDetails: selectedLocationDetails // Send detailed location info
            };
            await dispatch(requestJobVerification(jobData)).unwrap();
            setCaptchaInput('');
        } catch (err) {
            console.error('Failed to request verification:', err);
            generateLocalCaptcha();
            setCaptchaInput('');
        }
    };

    // Step 2: Verify OTP
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (!otpCode || otpCode.length !== 4) {
            setErrors({ otp: 'Please enter the 4-digit code' });
            return;
        }
        dispatch(clearError());
        try {
            await dispatch(verifyAndPostJob({ email: formData.email, code: otpCode })).unwrap();
        } catch (err) {
            console.error('Verification failed:', err);
        }
    };

    const handleResend = async () => {
        if (resendDisabled) return;
        setResendDisabled(true);
        dispatch(clearError());
        try {
            await dispatch(requestJobVerification({ ...formData, captchaInput: '' })).unwrap();
        } catch (err) {
            console.error('Resend failed:', err);
            setResendDisabled(false);
        }
    };

    const handleEditDetails = () => {
        setShowOtpStep(false);
        setOtpCode('');
        setErrors({});
        dispatch(clearError());
        setCaptchaInput('');
        generateLocalCaptcha();
    };

    const formatCountdown = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const styles = {
        container: { minHeight: '100vh', background: '#f5f5f5', padding: '2rem' },
        headerContainer: { textAlign: 'center', marginBottom: '2rem' },
        formContainer: { maxWidth: '700px', margin: '0 auto', background: 'white', borderRadius: '8px', padding: '2.5rem', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' },
        buttonGroup: { display: 'flex', gap: '1rem', marginBottom: '1.5rem' },
        backButton: { background: '#6c757d', border: 'none', color: 'white', fontSize: '0.9rem', cursor: 'pointer', padding: '0.5rem 1rem', borderRadius: '4px', flex: 1 },
        viewJobsButton: { background: '#28a745', border: 'none', color: 'white', fontSize: '0.9rem', cursor: 'pointer', padding: '0.5rem 1rem', borderRadius: '4px', flex: 1 },
        formHeader: { textAlign: 'center', marginBottom: '2rem' },
        formHeaderH3: { color: '#333', fontSize: '1.5rem', marginBottom: '0.5rem' },
        formHeaderP: { color: '#666', fontSize: '0.9rem' },
        formGroup: { marginBottom: '1.5rem' },
        label: { display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500', fontSize: '0.9rem' },
        required: { color: '#dc3545' },
        input: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' },
        textarea: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', resize: 'vertical', minHeight: '120px', boxSizing: 'border-box' },
        errorText: { color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' },
        submitButton: { width: '100%', padding: '12px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', fontSize: '1rem', cursor: 'pointer', marginTop: '1rem' },
        errorMessage: { background: '#f8d7da', color: '#721c24', padding: '1rem', borderRadius: '4px', marginBottom: '1.5rem', textAlign: 'center', fontSize: '0.9rem' },
        infoMessage: { background: '#d1ecf1', color: '#0c5460', padding: '1rem', borderRadius: '4px', marginBottom: '1.5rem', textAlign: 'center', fontSize: '0.9rem' },
        poweredBy: { textAlign: 'center', marginTop: '2rem' },
        poweredByText: { marginBottom: '0.5rem', fontSize: '0.8rem', color: '#666' },
        otpInput: { letterSpacing: '0.5rem', textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold' },
        resendContainer: { marginTop: '1rem', textAlign: 'center' },
        resendLink: { color: resendDisabled ? '#6c757d' : '#007bff', cursor: resendDisabled ? 'not-allowed' : 'pointer', fontSize: '0.9rem', textDecoration: 'underline' },
        countdownText: { fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' },
        captchaContainer: { marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' },
        captchaLabel: { display: 'block', marginBottom: '0.75rem', color: '#333', fontWeight: '500', fontSize: '0.9rem' },
        captchaImageContainer: { display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' },
        captchaImage: { border: '2px solid #dee2e6', borderRadius: '6px', cursor: 'pointer', height: '70px', width: '200px', background: '#fff' },
        refreshButton: { padding: '0.5rem 1rem', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
        captchaInput: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem', textTransform: 'uppercase', textAlign: 'center', letterSpacing: '4px', fontWeight: 'bold', boxSizing: 'border-box' },
        modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
        modalContent: { background: 'white', borderRadius: '8px', padding: '2rem', maxWidth: '500px', width: '90%', textAlign: 'center' },
        modalTitle: { fontSize: '1.5rem', color: '#28a745', marginBottom: '1rem' },
        modalText: { marginBottom: '1.5rem', color: '#666', lineHeight: '1.5' },
        jobList: { textAlign: 'left', marginBottom: '1.5rem', maxHeight: '200px', overflowY: 'auto', padding: '0.5rem', background: '#f8f9fa', borderRadius: '4px' },
        jobItem: { padding: '0.5rem', borderBottom: '1px solid #dee2e6' },
        locationNote: { fontSize: '0.75rem', color: '#666', marginTop: '0.25rem', fontStyle: 'italic' },
        validLocation: { fontSize: '0.75rem', color: '#28a745', marginTop: '0.25rem', fontStyle: 'italic' }
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerContainer}>
                <h1 style={{ margin: 0, fontSize: '2rem', color: '#333' }}>Are you interested in posting your drone project? </h1>
                <img src={UAVSphere_logo} alt="UAVSphere Logo" style={{ width: '180px', height: 'auto', marginTop: '1rem' }} />
            </div>
            
            <div style={styles.formContainer}>
                {!showOtpStep && (
                    <div style={styles.buttonGroup}>
                        <button style={styles.backButton} onClick={() => navigate('/')}>← Back to Home</button>
                        <button style={styles.viewJobsButton} onClick={() => navigate('/jobopenings')}>View All Project</button>
                    </div>
                )}
                
                <div style={styles.formHeader}>
                    <h3 style={styles.formHeaderH3}>{showOtpStep ? "Verify Your Email" : "Create New Drone Project Posting"}</h3>
                    <p style={styles.formHeaderP}>{showOtpStep ? `We've sent a 4-digit code to ${formData.email}` : "Connect with drone companies and professional pilots"}</p>
                </div>
                
                {/* Info message for pending jobs */}
                {totalPendingJobs > 0 && !showOtpStep && isUserVerified && (
                    <div style={styles.infoMessage}>ℹ️ You have {totalPendingJobs} pending job{totalPendingJobs > 1 ? 's' : ''} awaiting verification</div>
                )}
                
                {error && typeof error === 'string' && <div style={styles.errorMessage}>✗ {error}</div>}
                
                {!showOtpStep ? (
                    <form onSubmit={handleSubmit}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Project Title <span style={styles.required}>*</span></label>
                            <input type="text" name="name" value={formData.name} onChange={handleChange} style={styles.input} placeholder="Enter the project title" disabled={isSubmitting}/>
                            {errors.name && <span style={styles.errorText}>{errors.name}</span>}
                        </div>
                        
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Email Address <span style={styles.required}>*</span></label>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} style={styles.input} placeholder="Enter your email" disabled={isSubmitting}/>
                            {errors.email && <span style={styles.errorText}>{errors.email}</span>}
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Contact Number <span style={styles.required}>*</span></label>
                            <input type="tel" name="contactNumber" value={formData.contactNumber} onChange={handleChange} style={styles.input} placeholder="Enter your phone number" disabled={isSubmitting}/>
                            {errors.contactNumber && <span style={styles.errorText}>{errors.contactNumber}</span>}
                        </div>

                        {/* Enhanced Location Field with Google Places Autocomplete - FIXED */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Project Location <span style={styles.required}>*</span></label>
                            <GooglePlacesAutocomplete
                                apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                                selectProps={{
                                    value: locationValue,
                                    onChange: (value) => {
                                        setLocationValue(value);
                                        const locationName = value ? value.label : '';
                                        setFormData(prev => ({ ...prev, location: locationName }));
                                        
                                        // Extract location details
                                        if (value && value.value) {
                                            const locationParts = value.label.split(',');
                                            const city = locationParts[0]?.trim() || '';
                                            const country = locationParts[locationParts.length - 1]?.trim() || '';
                                            setSelectedLocationDetails({
                                                fullAddress: value.label,
                                                city: city,
                                                country: country,
                                                placeId: value.value.place_id || null
                                            });
                                        } else {
                                            setSelectedLocationDetails(null);
                                        }
                                        
                                        if (errors.location) {
                                            setErrors(prev => ({ ...prev, location: '' }));
                                        }
                                    },
                                    placeholder: "Search for a city or location...",
                                    styles: googlePlacesStyles,
                                    isClearable: true,
                                }}
                                autocompletionRequest={{
                                    types: ['(cities)'], // Use ONLY (cities) without mixing with other types
                                    // Remove componentRestrictions or keep it simple
                                }}
                            />
                            {selectedLocationDetails && (
                                <div style={styles.validLocation}>
                                    ✓ Valid location selected: {selectedLocationDetails.city}
                                    {selectedLocationDetails.country && `, ${selectedLocationDetails.country}`}
                                </div>
                            )}
                            {errors.location && <span style={styles.errorText}>{errors.location}</span>}
                            <div style={styles.locationNote}>
                                💡 Tip: Start typing your city name and select from the dropdown suggestions
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Project Description <span style={styles.required}>*</span></label>
                            <textarea name="description" value={formData.description} onChange={handleChange} style={styles.textarea} placeholder="Describe the project, requirements, and equipment needed..." disabled={isSubmitting}/>
                            {errors.description && <span style={styles.errorText}>{errors.description}</span>}
                        </div>
                        
                        {/* CAPTCHA Section */}
                        <div style={styles.captchaContainer}>
                            <label style={styles.captchaLabel}>Security Verification <span style={styles.required}>*</span></label>
                            <div style={styles.captchaImageContainer}>
                                {localCaptchaImage ? (
                                    <img src={localCaptchaImage} alt="CAPTCHA" style={styles.captchaImage} onClick={generateLocalCaptcha} title="Click to refresh"/>
                                ) : (
                                    <div style={styles.captchaPlaceholder}>Loading...</div>
                                )}
                                <button type="button" style={styles.refreshButton} onClick={() => { setCaptchaInput(''); generateLocalCaptcha(); }}>🔄 Refresh</button>
                            </div>
                            <input type="text" value={captchaInput} onChange={(e) => setCaptchaInput(e.target.value.toUpperCase())} placeholder="Enter the code above" style={styles.captchaInput} maxLength={6} disabled={isSubmitting}/>
                            {errors.captcha && <span style={styles.errorText}>{errors.captcha}</span>}
                        </div>
                        
                        <button type="submit" disabled={isSubmitting || !localCaptchaImage} style={{ ...styles.submitButton, opacity: (isSubmitting || !localCaptchaImage) ? 0.7 : 1, cursor: (isSubmitting || !localCaptchaImage) ? 'not-allowed' : 'pointer' }}>
                            {isSubmitting ? 'Processing...' : 'Submit Drone Project Posting'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleVerifyOtp}>
                        <div style={styles.formGroup}>
                            <label style={{...styles.label, textAlign: 'center'}}>Enter 4-Digit Verification Code</label>
                            <input type="text" maxLength="4" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g,''))} style={{...styles.input, ...styles.otpInput}} placeholder="0000" disabled={isSubmitting} autoFocus/>
                            {errors.otp && <span style={{...styles.errorText, textAlign: 'center'}}>{errors.otp}</span>}
                        </div>
                        <button type="submit" disabled={isSubmitting} style={{ ...styles.submitButton, opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                            {isSubmitting ? 'Verifying...' : 'Verify & Post Project'}
                        </button>
                        <div style={styles.resendContainer}>
                            <span style={styles.resendLink} onClick={!resendDisabled ? handleResend : undefined}>Didn't get a code? Resend Email</span>
                            {countdown > 0 && <div style={styles.countdownText}>Code expires in: {formatCountdown(countdown)}</div>}
                        </div>
                        <button type="button" onClick={handleEditDetails} style={{ ...styles.backButton, width: '100%', marginTop: '1.5rem', background: 'transparent', color: '#666', border: '1px solid #ccc' }} disabled={isSubmitting}>← Edit Project Details</button>
                    </form>
                )}
            </div>
            
            {/* Success Modal */}
            {showSuccessModal && (
                <div style={styles.modal} onClick={() => setShowSuccessModal(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalTitle}>{isUserVerified ? 'Project Staged Successfully!' : 'Projects Posted Successfully!'}</div>
                        <div style={styles.modalText}>
                            {isUserVerified ? (
                                <p>Your project has been staged and will be posted automatically. Thank you for your submission!</p>
                            ) : (
                                <>
                                    <p>Your project{createdJobs.length > 1 ? 's have' : ' has'} been submitted successfully and {createdJobs.length > 1 ? 'are' : 'is'} now awaiting admin review.</p>
                                    {createdJobs.length > 0 && (
                                        <div style={styles.jobList}>
                                            <strong>Submitted Projects:</strong>
                                            {createdJobs.map((job, index) => (
                                                <div key={index} style={styles.jobItem}><strong>{job.job_title}</strong><br/><small>{job.location}</small></div>
                                            ))}
                                        </div>
                                    )}
                                    <p>You will be notified once your project posting{createdJobs.length > 1 ? 's are' : ' is'} approved.</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Powered By Section */}
            <div style={styles.poweredBy}>
                <p style={styles.poweredByText}>
                    Powered by GeoWGS84 <br/>
                    <a href="https://www.geowgs84.com/" target="_blank" rel="noopener noreferrer">
                        <img src={Powered_by_logo} alt="Company Logo" style={{ width: '30px', height: 'auto', marginTop: '5px' }} />
                    </a>
                </p>
            </div>
        </div>
    );
};
